import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FETCH_ERROR, FETCH_START, FETCH_SUCCESS } from '../../constants/ActionTypes';
import customAxios from '../../util/Api';
import authHeader from '../../../../services/auth/jwt/auth-header';
import { flags } from '@jumbo/components/AppLayout/partials/LanguageSwitcher/data';
import { useToast } from 'context/tostifyContext';
import useUrlParam from 'services/globalHooks/useUrlParam';

const INITIAL_SITEDATA_STATE = {
  checklist: [],
  geolocations: [],
  harvesting: [],
  hazards: [],
  info: [],
  products: [],
  species: [],
};

const NOSECTION_SELECT = {
  sectionName: '',
  sectionData: '',
};

const useSiteContract = () => {
  const [siteContractsData, setSiteContractsData] = useState([]);
  const [inactiveSiteContractData, setInactiveSiteContractData] = useState([]);
  const [selectedContract, setSelectedContract] = useState({});
  const [showMap, setShowMap] = useState(false);
  const [showRoute, setShowRoute] = useState(false);

  const dispatch = useDispatch();
  const isMounted = useRef(true);

  // Edit dialog states
  const [openContractInfoViewDialog, setOpenContractInfoViewDialog] = useState(false);
  const [contractInfoData, setContractInfoData] = useState({});
  const [isSiteInfo, setIsSiteInfo] = useState(false);
  const [openEditContractInfoDialog, setOpenEditContractInfoDialog] = useState(false);
  const [selectedContractSection, setSelectedContractSection] = useState(NOSECTION_SELECT);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState(null);

  // Relation
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [assignParentContract, setAssignParentContract] = useState(null);
  const [childContracts, setChildContracts] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [allRelations, setAllRelations] = useState([]);

  const { showToast } = useToast();

  const { getParam, setParam, deleteParam } = useUrlParam();

  // Fixed useEffect - remove setter functions from dependencies
  useEffect(() => {
    getSiteContracts();
  }, []); // Empty dependency array since we only want this to run once on mount

  useEffect(() => {
    const contractIdFromURL = getParam('contractUniqueID');
    if (contractIdFromURL && !openEditContractInfoDialog && siteContractsData.length > 0) {
      const contractToOpen = siteContractsData.find(
        contract => String(contract.contractUniqueID) === String(contractIdFromURL),
      );
      if (contractToOpen) {
        // Use a functional wrapper to make sure state setter picks up latest data
        setOpenContractInfoViewDialog(true);
        setSelectedContract(contractToOpen);
      }
    }
  }, [siteContractsData, getParam, openEditContractInfoDialog]);

  const getSiteContracts = async () => {
    try {
      dispatch({ type: FETCH_START });

      const [activeResponse, inactiveResponse, whRelationResponse] = await Promise.all([
        customAxios.get('/Ops/GetContracts?flagActive=1', { headers: authHeader() }),
        customAxios.get('/Ops/GetContracts?flagActive=0', { headers: authHeader() }),
        customAxios.get('/Ops/GetWHRelation', { headers: authHeader() }),
      ]);

      if (isMounted.current) {
        const relations = Array.isArray(whRelationResponse.data) ? whRelationResponse.data : [];

        // parentSP  → [childSP, ...]   (to show children under a parent row)
        const childrenMap = {};
        // childSP   → parentSP          (to show the parent under a child row)
        const parentMap = {};

        for (const rel of relations) {
          if (!childrenMap[rel.parentSP]) childrenMap[rel.parentSP] = [];
          childrenMap[rel.parentSP].push(rel.childSP);
          parentMap[rel.childSP] = rel.parentSP;
        }

        setAllRelations(relations);

        const stampRelation = contract => {
          const id = contract.contractUniqueID;
          const isParent = !!childrenMap[id];
          const isChild = !!parentMap[id];

          let roleLabel = '';
          let relationLabel = '';

          if (isParent) {
            roleLabel = 'Parent';
            relationLabel = childrenMap[id].join(' | ');
          } else if (isChild) {
            roleLabel = 'Child';
            relationLabel = parentMap[id];
          }

          // Strip "(Default version)" or any trailing "(...)" from pricingVersion
          const cleanedPricingVersion = contract.pricingVersion
            ? contract.pricingVersion.replace(/\s*\(.*?\)\s*$/, '').trim()
            : contract.pricingVersion;

          return { 
            ...contract, 
            pricingVersion: cleanedPricingVersion,
            roleLabel, 
            relationLabel 
          };
        };

        setSiteContractsData(activeResponse.data.map(stampRelation));
        setInactiveSiteContractData(inactiveResponse.data.map(stampRelation));
        dispatch({ type: FETCH_SUCCESS });
      }
    } catch (error) {
      if (isMounted.current) {
        setSiteContractsData([]);
        setInactiveSiteContractData([]);
        console.error('error', error);
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
      }
    }
  };

  async function saveSiteRef(contractMainData, siteRef) {
    const contractPayload = {
      ...contractMainData,
      wH_NumberName: siteRef,
    };

    try {
      const res = await customAxios.post('/Ops/EditContract', contractPayload, {
        headers: authHeader(),
      });

      if (res.status === 200) {
        getSiteContracts();
        setSelectedContract({ ...contractMainData, wH_NumberName: siteRef });
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function updateStartDate(contractMainData, startDate) {
    const contractPayload = {
      ...contractMainData,
      startDate: startDate,
    };

    const res = customAxios.post('/Ops/EditContract', contractPayload, {
      headers: authHeader(),
    });
    getSiteContracts();
    setSelectedContract({ ...contractMainData, startDate: startDate });
  }
  async function updateFinishDate(contractMainData, finishDate) {
    const contractPayload = {
      ...contractMainData,
      finishDate: finishDate,
    };
    const res = customAxios.post('/Ops/EditContract', contractPayload, {
      headers: authHeader(),
    });
    getSiteContracts();
    setSelectedContract({ ...contractMainData, finishDate: finishDate });
  }

  const getSiteContractFullData = async contract => {
    dispatch({ type: FETCH_START });

    const contractId = typeof contract === 'object' ? contract.contractUniqueID : contract;

    // preserve selected contract state
    if (typeof contract === 'object') {
      setSelectedContract(contract);
    } else {
      setSelectedContract(prev => ({
        ...prev,
        contractUniqueID: contract,
      }));
    }

    setOpenContractInfoViewDialog(true);
    setIsSiteInfo(false);

    setParam('contractUniqueID', contractId);

    const pricingVerNumber = contract?.pricingVersion?.split(' (')[0];
    try {
      const [contractOptionsRes, timberPricingRes, questionnaireRes, accountInfoRes, notificationRes] = await Promise.all([
        customAxios.get(`/Ops/GetContractsOptions/${contractId}`, {
          headers: authHeader(),
        }),
        customAxios.get(`/Ops/GetTimberPricing?pricingVersion=${pricingVerNumber}`, {
          headers: authHeader(),
        }),
        customAxios.get(`/Ops/GetContractsQuestionnaire?contractUniqueID=${contractId}`, { headers: authHeader() }),
        customAxios.get(`/Ops/GetContractsAccountingInfo?contractUniqueID=${contractId}`, { headers: authHeader() }),
        customAxios.get(`/Ops/GetContractsUsersToNotify?contractUniqueID=${contractId}`, { headers: authHeader() }),
      ]);

      if (contractOptionsRes.status === 200) {
        const questionnaire = Array.isArray(questionnaireRes.data) ? questionnaireRes.data : [questionnaireRes.data];

        const accountInfo = Array.isArray(accountInfoRes.data) ? accountInfoRes.data : [accountInfoRes.data];

        console.log('timberPricingResponse ===', timberPricingRes);
        const timberPricingRates = timberPricingRes.data.map(item => ({
          product: item.product,
          destination: item.destination,
          haulage: item.haulage,
          harvesting: item.harvestingCostsPerTonne,
          extraction: item.extractionCosts,
          purchase: item.purchasesPrice,
          tonnes: item.tonnes,
        }));

        const notificationContact = notificationRes?.data?.map(item => ({
          growerContact: item.emailOrPhone,
          notificationSection: item.notificationSection,
        }));

        console.log('CONTRACT OPTIONS RES ====', contract);

        setContractInfoData({
          ...contractOptionsRes.data,
          contractInfo: [contract],
          timberPricingRates: timberPricingRates,
          contractQuestionnaire: questionnaire,
          accountInfo,
          notificationContact,
        });

        dispatch({ type: FETCH_SUCCESS });
      } else {
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
      }
    } catch (err) {
      dispatch({ type: FETCH_ERROR, payload: err.message });
    }
  };

  const handleSiteInfoClose = () => {
    setOpenContractInfoViewDialog(false);
    setSelectedContract({});
    setContractInfoData({});
    deleteParam('contractUniqueID');
  };

  const getFullSiteInfo = async contract => {
    if (!contract.siteAssessmentID) {
      alert('No Site assessment id found');
      return;
    }
    dispatch({ type: FETCH_START });
    setSelectedContract(contract);
    setOpenContractInfoViewDialog(true);
    setIsSiteInfo(true);

    try {
      const response = await customAxios.get('/SiteDetails/GetSiteOptions/' + contract.siteAssessmentID, {
        headers: authHeader(),
      });

      if (response.status === 200) {
        let currData = response.data;
        dispatch({ type: FETCH_SUCCESS });
        setContractInfoData({ ...currData });
      } else {
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
      }
    } catch (error) {
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  const handleOpenContractInfoEditModal = (sectionName, sectionData) => {
    setOpenEditContractInfoDialog(true);
    setSelectedContractSection({
      sectionData: sectionData,
      sectionName: sectionName,
    });
  };

  const handleContractInfoEditModalClose = () => {
    setOpenEditContractInfoDialog(false);
    setSelectedContractSection(NOSECTION_SELECT);
  };

  const handleEditSaveContractSection = async editedData => {
    dispatch({ type: FETCH_START });

    let currSectionName = selectedContractSection.sectionName;
    let editUrl = '';

    if (currSectionName === 'coillteInfo') {
      editUrl = '/Ops/EditContractCoillte';
    } else if (currSectionName == 'certificationInfo') {
      editUrl = '/Ops/EditContractCertificationInfo';
    } else if (currSectionName === 'invoicingRates') {
      editUrl = '/Ops/EditContractInvoicingRates';
    } else if (currSectionName === 'productDetails') {
      editUrl = '/Ops/EditContractProductDetails';
    } else if (currSectionName === 'rates') {
      editUrl = '/Ops/EditContractRates';
    } else if (currSectionName === 'contractQuestionnaire') {
      editUrl = '/Ops/EditContractsQuestionnaire';
    } else if (currSectionName === 'contractQuestionnaire') {
      editUrl = '/Ops/EditContractsQuestionnaire';
    } else if (currSectionName === 'accountInfo') {
      editUrl = 'Ops/EditContractsAccountingInfo';
    }

    try {
      const response = await customAxios.post(editUrl, editedData, {
        headers: authHeader(),
      });

      if (response.status === 200) {
        dispatch({ type: FETCH_SUCCESS });
        await getSiteContractFullData(selectedContract);
        handleContractInfoEditModalClose();
      } else {
        dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
      }
    } catch (error) {
      dispatch({ type: FETCH_ERROR, payload: 'Network Error' });
    }
  };

  // MODIFIED: Updated handleContractStatus to refresh data on the fly
  const handleContractStatus = async (contractId, contractStatus) => {
    try {
      dispatch({ type: FETCH_START });

      let currStatus = contractStatus ? 0 : 1;
      const payload = {
        contractUniqueID: contractId,
        flagActive: currStatus,
      };

      // Wait for the status update to complete
      await customAxios.post('/Ops/UpdateContractStatus', payload, {
        headers: authHeader(),
      });

      // Update selectedContract immediately with new status
      setSelectedContract(prev => ({
        ...prev,
        flagActive: currStatus,
      }));

      // Refresh both active and inactive contract lists to reflect changes
      await getSiteContracts();

      // MODIFIED: If a contract dialog is open, refresh the contractInfoData only
      // Don't call getSiteContractFullData as it would overwrite selectedContract
      if (openContractInfoViewDialog && selectedContract.contractUniqueID === contractId) {
        try {
          const response = await customAxios.get(`/Ops/GetContractsOptions/${contractId}`, {
            headers: authHeader(),
          });
          if (response.status === 200) {
            setContractInfoData({ ...response.data });
          }
        } catch (err) {
          console.error('Error refreshing contract info:', err);
        }
      }

      dispatch({ type: FETCH_SUCCESS });
    } catch (error) {
      console.error('Error updating contract status:', error);
      dispatch({ type: FETCH_ERROR, payload: 'Failed to update contract status' });
    }
  };

  // Optional: Add a refresh function that can be called manually
  const refreshContractData = () => {
    getSiteContracts();
  };

  async function handleDeleteContract(contractUniqueId) {
    if (!contractUniqueId) {
      showToast('No contract ID found', 'Error');
      return;
    }
    try {
      dispatch({ type: FETCH_START });
      const res = await customAxios.post(
        '/Ops/DeleteContract',
        { contractUniqueID: contractUniqueId },
        { headers: authHeader() },
      );
      if (res.status === 200) {
        showToast(`Contract ${contractUniqueId} deleted successfully`);
        getSiteContracts();
        setOpenDeleteModal(false);
      }
    } catch (err) {
      console.log(err);
      showToast('Error in deleting contract', 'error');
      setOpenDeleteModal(false);
    }
  }

  // Relation
  const handleOpenAssignModal = async rowData => {
    setAssignParentContract(rowData);
    setOpenAssignModal(true);
    await fetchChildContracts(rowData.contractUniqueID);
  };

  const fetchChildContracts = async parentId => {
    setLoadingChildren(true);
    try {
      const res = await customAxios.get(`/Ops/GetWHRelation?parentSP=${parentId}`, { headers: authHeader() });
      setChildContracts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching child contracts:', err);
      setChildContracts([]);
    } finally {
      setLoadingChildren(false);
    }
  };

  /**
   * Save new child relations for a parent.
   */
  const handleSaveContractRelations = async (parentId, selectedChildren) => {
    try {
      dispatch({ type: FETCH_START });

      for (const child of selectedChildren) {
        const payload = {
          parentSP: parentId,
          childSP: child.contractUniqueID,
          details: child.wH_NumberName ?? '',
          contractType: child.contractType ?? '',
        };
        await customAxios.post('/Ops/AddWHRelation', payload, { headers: authHeader() });
      }

      await fetchChildContracts(parentId);
      await getSiteContracts();
      showToast('Child contracts assigned successfully');
      dispatch({ type: FETCH_SUCCESS });
    } catch (err) {
      console.error(err);
      showToast('Error saving contract relations', 'error');
      dispatch({ type: FETCH_ERROR, payload: 'Error saving relations' });
    }
  };

  /**
   * Remove a single child relation.
   */
  const handleDeleteContractRelation = async (parentId, relationId) => {
    try {
      dispatch({ type: FETCH_START });
      await customAxios.post('/Ops/DeleteWHRelation', { relationId }, { headers: authHeader() });
      await fetchChildContracts(parentId);
      await getSiteContracts();
      showToast('Relation removed');
      dispatch({ type: FETCH_SUCCESS });
    } catch (err) {
      console.error(err);
      showToast('Error removing relation', 'error');
      dispatch({ type: FETCH_ERROR, payload: 'Error removing relation' });
    }
  };

  return {
    siteContractsData,
    setSiteContractsData,
    getSiteContractFullData,
    openContractInfoViewDialog,
    setOpenContractInfoViewDialog,
    selectedContract,
    contractInfoData,
    setContractInfoData,
    getFullSiteInfo,
    isSiteInfo,
    openEditContractInfoDialog,
    setOpenEditContractInfoDialog,
    handleOpenContractInfoEditModal,
    selectedContractSection,
    handleEditSaveContractSection,
    handleContractStatus,
    inactiveSiteContractData,
    refreshContractData,
    saveSiteRef,
    updateFinishDate,
    updateStartDate,
    showMap,
    setShowMap,
    showRoute,
    setShowRoute,
    handleDeleteContract,
    openDeleteModal,
    setOpenDeleteModal,
    selectedContractId,
    setSelectedContractId,

    openAssignModal,
    setOpenAssignModal,
    assignParentContract,
    childContracts,
    loadingChildren,
    handleOpenAssignModal,
    handleSaveContractRelations,
    handleDeleteContractRelation,
    allRelations,
    handleSiteInfoClose,
  };
};

export default useSiteContract;
